import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Menu } from "@headlessui/react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  File,
  FileDown,
  FolderInput,
  FolderPlus,
  GripVertical,
  MoreVertical,
  RefreshCw,
  Search,
  Trash2,
  UploadCloud,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  buildContainerItems,
  compareLibraryGames,
  isCurrentGame,
  libraryItemId,
  type LibraryItem,
} from "./library-order.ts";

/** The MOFO sigma brand mark — same icon the QB REPORT button carries, and
 *  the same one platform-2.0 shows on its Post-Game kebab entry. */
const SigmaIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 31 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M19.8881 5.63976C20.3125 5.53478 20.8816 5.56102 21.5988 5.7185C26.9316 6.9357 29.8464 10.0295 30.3531 15.0033C30.5636 17.0669 30.3202 19.0322 29.6161 20.8957C28.9121 22.7592 27.8857 24.3537 26.5336 25.6759C25.1814 26.998 23.5694 28.0512 21.7008 28.8287C19.8322 29.6096 17.8747 30 15.8317 30C13.0979 30 10.5811 29.3307 8.27169 27.9954C5.96222 26.6601 4.13966 24.8425 2.80069 22.5394C1.46173 20.2362 0.790604 17.7264 0.790604 15C0.790604 12.2736 1.46173 9.76378 2.80069 7.46063C4.13966 5.16076 5.96222 3.3399 8.27169 2.00459C10.5779 0.669291 13.0979 0 15.8317 0H30.7906C30.738 0.104987 30.6656 0.259186 30.5702 0.456037C30.4781 0.652887 30.2642 0.997375 29.932 1.48622C29.5997 1.97507 29.2477 2.40486 28.8759 2.77559C28.5042 3.14633 28.014 3.49081 27.4054 3.80577C26.7935 4.12402 26.1585 4.2815 25.494 4.2815H15.8251C12.8544 4.2815 10.3213 5.32808 8.22563 7.4147C6.13 9.50459 5.08384 11.9759 5.08384 14.9934C5.08384 17.956 6.06466 20.4018 8.22563 22.5722C10.7002 24.8415 12.8544 25.7054 15.8251 25.7054C18.7959 25.7054 21.289 24.702 23.4247 22.5722C25.5203 20.4823 26.5665 17.9159 26.5665 14.9934C26.5665 12.6903 25.9743 10.748 24.7965 9.1601C23.6155 7.57218 21.9837 6.40748 19.8881 5.63976Z"
      fill="#007BFF"
    />
  </svg>
);

import {
  type FolderDto,
  type FolderTreeNode,
  MAX_FOLDER_DEPTH,
  buildFolderTree,
} from "./folder.types.ts";
import type { LibraryGame } from "./adapter.ts";
import { MacFolderIcon } from "./MacFolderIcon.tsx";
import { useToday } from "../../../hooks/useToday.ts";
import { GameDateCell } from "./GameDateCell.tsx";
import { GameWeekCell } from "./GameWeekCell.tsx";
import { wasTouchTap } from "../../../lib/touch.ts";

// Practice version: in-progress games are reserved under a fixed label so
// they don't collide with user-named completed games. Mirrors the in-game app.
const RESERVED_GAME_NAME = "Current Game";
const isReservedGameName = (name: string): boolean =>
  name.trim().toLowerCase() === RESERVED_GAME_NAME.toLowerCase();

type Props = {
  folders: FolderDto[];
  games: LibraryGame[];
  gameCountByFolderId: Map<number, number>;
  onFolderCreate: (parentId: number | null) => void;
  onFolderRename: (folderId: number, nextName: string) => Promise<void> | void;
  onFolderDelete: (folderId: number) => void;
  onFolderMove?: (folderId: number) => void;
  /** Anchored parent for the inline "New folder" input. */
  creatingUnder?: number | null;
  pendingName?: string;
  onPendingNameChange?: (next: string) => void;
  onPendingNameSubmit?: () => void;
  onPendingNameCancel?: () => void;
  /** Set of selected game IDs (drives checkbox state + bulk actions). */
  selectedGameIds: Set<number>;
  onToggleGameSelected: (gameId: number) => void;
  onToggleSelectAll: (nextSelected: boolean, visibleIds: number[]) => void;
  /** Persists an inline game rename. */
  onGameRename: (
    gameId: number,
    nextName: string,
    complete: boolean,
  ) => Promise<void> | void;
  /**
   * Moves a saved game to another day (its date cell opens a date picker);
   * resolves true once the server has it. Omit to keep dates read-only.
   */
  onGameDateChange?: (gameId: number, gameDate: string) => Promise<boolean>;
  onGameMove: (gameId: number) => void;
  onGameDelete: (gameId: number, complete: boolean) => void;
  /**
   * Drag-reorder persistence: fires with the container the drag happened in
   * and its complete new MIXED item order ("folder-N" / "game-N" ids —
   * games and folders share one list per level). Omit to disable dragging.
   */
  onReorderItems?: (
    containerId: number | null,
    orderedItemIds: string[],
  ) => void;
  onBulkMove?: (gameIds: number[]) => void;
  onBulkDelete?: (gameIds: number[]) => void;
  /**
   * Optional export actions, mirroring the in-game app's kebab. Each shows
   * on game rows AND in the bulk kebab (multi-select = merged export) only
   * when its handler is provided.
   */
  onGameReport?: (gameIds: number[]) => void;
  reportLabel?: string;
  /**
   * When true for a selection, a generated report is cached: the Report
   * item previews it, and Download/Regenerate entries appear (mirrors the
   * in-game kebab once a post-game report exists).
   */
  isReportReady?: (gameIds: number[]) => boolean;
  onReportDownload?: (gameIds: number[]) => void;
  onReportRegenerate?: (gameIds: number[]) => void;
  onGameDownload?: (gameIds: number[]) => void;
  onGameExportToDrive?: (gameIds: number[]) => void;
};

/**
 * A game's Date and Week cells. The Week is the week of the day the Date
 * shows — a pick still saving, or one saved before the list catches up — so
 * the two never disagree (client 2026-11-04).
 */
function GameDateAndWeekCells({
  game,
  onSave,
  today,
}: {
  game: LibraryGame;
  onSave?: (gameId: number, gameDate: string) => Promise<boolean>;
  /** The current game's day (useToday, once for the table). */
  today: string;
}) {
  const [shownDay, setShownDay] = useState<string | null>(null);
  return (
    <>
      <td className="relative w-px whitespace-nowrap px-6 py-2 text-sm text-gray-600">
        {/* UTC timestamp from the API, shown as the local day; a saved
            game's opens the date picker (in-game parity). */}
        <GameDateCell game={game} onSave={onSave} onShownDay={setShownDay} today={today} />
      </td>
      <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-600">
        {/* The practice week (Mon–Sun). Takes the table's spare width, so it
            sits right after the date. */}
        <GameWeekCell game={game} day={shownDay} today={today} />
      </td>
    </>
  );
}

const NAME_INDENT_PX = 16;
const NAME_BASE_PADDING_PX = 16;

export const GameLibraryTreeView: React.FC<Props> = ({
  folders,
  games,
  gameCountByFolderId,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderMove,
  creatingUnder,
  pendingName,
  onPendingNameChange,
  onPendingNameSubmit,
  onPendingNameCancel,
  selectedGameIds,
  onToggleGameSelected,
  onToggleSelectAll,
  onGameRename,
  onGameDateChange,
  onGameMove,
  onGameDelete,
  onReorderItems,
  onBulkMove,
  onBulkDelete,
  onGameReport,
  reportLabel,
  isReportReady,
  onReportDownload,
  onReportRegenerate,
  onGameDownload,
  onGameExportToDrive,
}) => {
  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  // FolderTreeNode by id — mixed items carry FolderDto; rendering needs the
  // tree node (depth + children).
  const nodeByFolderId = useMemo(() => {
    const map = new Map<number, FolderTreeNode>();
    const visit = (node: FolderTreeNode) => {
      map.set(node.folder.folder_id, node);
      node.children.forEach(visit);
    };
    tree.forEach(visit);
    return map;
  }, [tree]);
  // The current game's day in its Date and Week cells, turning over at midnight.
  const today = useToday();
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const folderNameMatches = (f: { name?: string | null }) =>
    !!q && (f.name ?? "").toLowerCase().includes(q);

  const gamesByFolder = useMemo(() => {
    const map = new Map<number | null, LibraryGame[]>();
    for (const g of games) {
      const fid = g.folder_id ?? null;
      const arr = map.get(fid) ?? [];
      arr.push(g);
      map.set(fid, arr);
    }
    for (const arr of map.values()) {
      arr.sort(compareLibraryGames);
    }
    return map;
  }, [games]);

  const matchedGameIds = useMemo(() => {
    if (!q) return null;
    const set = new Set<number>();
    for (const g of games) {
      if (
        g.game_id != null &&
        (g.game_description ?? "").toLowerCase().includes(q)
      ) {
        set.add(g.game_id);
      }
    }
    return set;
  }, [q, games]);

  const shownFolderIds = useMemo(() => {
    if (!q) return null;
    const set = new Set<number>();
    const visit = (node: FolderTreeNode): boolean => {
      const selfMatch = (node.folder.name ?? "").toLowerCase().includes(q);
      const hasMatchingGame = (gamesByFolder.get(node.folder.folder_id) ?? []).some(
        (g) => g.game_id != null && matchedGameIds?.has(g.game_id),
      );
      let childShown = false;
      for (const child of node.children) {
        if (visit(child)) childShown = true;
      }
      const shown = selfMatch || hasMatchingGame || childShown;
      if (shown) set.add(node.folder.folder_id);
      return shown;
    };
    for (const root of tree) visit(root);
    return set;
  }, [q, tree, gamesByFolder, matchedGameIds]);

  const gamesToShow = (
    folderId: number | null,
    folder?: { name?: string | null },
  ): LibraryGame[] => {
    const all = gamesByFolder.get(folderId) ?? [];
    if (!q) return all;
    if (folder && folderNameMatches(folder)) return all;
    return all.filter(
      (g) => g.game_id != null && matchedGameIds?.has(g.game_id),
    );
  };

  const visibleGameIds = useMemo(() => {
    const folderById = new Map(folders.map((f) => [f.folder_id, f]));
    if (q) {
      const ids: number[] = [];
      for (const g of games) {
        if (g.game_id == null) continue;
        const fid = g.folder_id ?? null;
        if (fid !== null && !shownFolderIds?.has(fid)) continue;
        const folder = fid !== null ? folderById.get(fid) : undefined;
        const folderMatch =
          !!folder && (folder.name ?? "").toLowerCase().includes(q);
        if (matchedGameIds?.has(g.game_id) || folderMatch) ids.push(g.game_id);
      }
      return ids;
    }
    const isAncestorPathOpen = (
      folderId: number | null,
      seen: Set<number>,
    ): boolean => {
      if (folderId === null) return true;
      if (seen.has(folderId)) return false;
      if (!expanded.has(folderId)) return false;
      const f = folderById.get(folderId);
      if (!f) return false;
      seen.add(folderId);
      return isAncestorPathOpen(f.parent_id ?? null, seen);
    };
    const ids: number[] = [];
    for (const g of games) {
      if (
        g.game_id != null &&
        isAncestorPathOpen(g.folder_id ?? null, new Set())
      ) {
        ids.push(g.game_id);
      }
    }
    return ids;
  }, [games, folders, expanded, q, shownFolderIds, matchedGameIds]);

  const allSelected =
    visibleGameIds.length > 0 &&
    visibleGameIds.every((id) => selectedGameIds.has(id));
  const someSelected =
    !allSelected && visibleGameIds.some((id) => selectedGameIds.has(id));

  const childCountByFolderId = useMemo(() => {
    const counts = new Map<number, number>();
    for (const f of folders) {
      if (f.parent_id != null) {
        counts.set(f.parent_id, (counts.get(f.parent_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [folders]);

  // ----- Drag-reorder (left handles, ported from the in-game library) -----
  // Disabled while searching: the filtered list isn't the real sibling order.
  const dndDisabled = q !== "";
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  // Container membership for drop validation: a folder lives under its
  // parent, a game under its folder. The current game belongs to no list.
  const containerOfRowId = (rowId: string): string | null => {
    if (rowId.startsWith("folder-")) {
      const f = folders.find(
        (x) => x.folder_id === Number(rowId.slice("folder-".length)),
      );
      return f ? `c:${f.parent_id ?? "root"}` : null;
    }
    const gid = Number(rowId.slice("game-".length));
    const g = games.find((x) => x.game_id === gid);
    if (!g || isCurrentGame(g)) return null;
    return `c:${g.folder_id ?? "root"}`;
  };

  // Mixed display list (folders + games interleaved on one position scale)
  // for one container; the pinned current game is NOT part of it.
  const containerItems = (containerId: number | null): LibraryItem[] =>
    buildContainerItems(folders, games, containerId);

  // The pinned current game row(s) of a container — rendered above the
  // mixed list, never draggable.
  const pinnedGamesFor = (
    containerId: number | null,
    folder?: { name?: string | null },
  ): LibraryGame[] => {
    const pinned = games.filter(
      (g) =>
        isCurrentGame(g) &&
        g.game_id != null &&
        (g.folder_id ?? null) === containerId,
    );
    if (!q) return pinned;
    if (folder && folderNameMatches(folder)) return pinned;
    return pinned.filter(
      (g) => g.game_id != null && matchedGameIds?.has(g.game_id),
    );
  };

  // Search-filtered mixed items for rendering (mirrored by sortableRowIds).
  const itemsToRender = (
    containerId: number | null,
    folder?: { name?: string | null },
  ): LibraryItem[] => {
    const items = containerItems(containerId);
    if (!q) return items;
    const shownGames = new Set(
      gamesToShow(containerId, folder).map((g) => g.game_id),
    );
    return items.filter((item) =>
      item.kind === "folder"
        ? shownFolderIds?.has(item.folder.folder_id)
        : shownGames.has(item.game.game_id),
    );
  };

  // Every rendered row id, in render order — required by SortableContext.
  const sortableRowIds = useMemo(() => {
    const ids: string[] = [];
    const nodeVisit = (containerId: number | null, folder?: FolderDto) => {
      for (const g of pinnedGamesFor(containerId, folder)) {
        ids.push(`game-${g.game_id}`);
      }
      for (const item of itemsToRender(containerId, folder)) {
        if (item.kind === "game") {
          ids.push(libraryItemId(item));
        } else {
          ids.push(libraryItemId(item));
          const isOpen = q ? true : expanded.has(item.folder.folder_id);
          if (isOpen) nodeVisit(item.folder.folder_id, item.folder);
        }
      }
    };
    nodeVisit(null);
    return ids;
    // itemsToRender/pinnedGamesFor read only values listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    folders,
    games,
    q,
    shownFolderIds,
    expanded,
    gamesByFolder,
    matchedGameIds,
  ]);

  // A drop reorders the MIXED list of one container — a game can land
  // between folders and vice versa. Cross-container drops are ignored
  // (moving between folders stays on the kebab's Move action), and the
  // pinned current game can neither be dragged nor displaced.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!onReorderItems) return;
    const a = String(active.id);
    const o = String(over.id);
    const containerA = containerOfRowId(a);
    if (containerA == null || containerA !== containerOfRowId(o)) return;
    const containerId =
      containerA === "c:root" ? null : Number(containerA.slice(2));
    const ids = containerItems(containerId).map(libraryItemId);
    const from = ids.indexOf(a);
    const to = ids.indexOf(o);
    if (from === -1 || to === -1) return;
    onReorderItems(containerId, arrayMove(ids, from, to));
  };

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const [editing, setEditing] = useState<
    { type: "folder"; folderId: number } | { type: "game"; gameId: number } | null
  >(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const skipBlurCommitRef = useRef(false);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditFolder = (folder: FolderDto) => {
    setEditing({ type: "folder", folderId: folder.folder_id });
    setEditingValue(folder.name);
    setEditingError(null);
  };
  const startEditGame = (game: LibraryGame) => {
    if (game.game_id == null) return;
    if (!game.complete) return;
    setEditing({ type: "game", gameId: game.game_id });
    setEditingValue(game.game_description ?? "");
    setEditingError(null);
  };
  // Double-TAP a name to rename it on the iPad. The kebab no longer has a
  // Rename item (in-game parity), so this is the ONLY way to rename there,
  // and iPad Safari does not reliably turn two taps into a `dblclick` —
  // emulated iPad WebKit fires two clicks and no dblclick at all. Same
  // approach as PracticeButton: time the taps ourselves (400ms), and ignore a
  // touch that was a scroll. onDoubleClick stays for the mouse.
  const lastNameTapRef = useRef<{ key: string; at: number } | null>(null);
  const onNameTouchEnd =
    (key: string, startEdit: () => void) => (event: React.TouchEvent) => {
      if (!wasTouchTap()) return;
      const now = Date.now();
      const last = lastNameTapRef.current;
      if (last && last.key === key && now - last.at < 400) {
        lastNameTapRef.current = null;
        // Swallow this tap: a real iPad would also fire its own dblclick and
        // a mousedown at the same spot — on the edit box that just opened. For
        // a long name that lands outside the box, blurs it and closes the
        // editor again. PracticeButton does the same for the same reason.
        event.preventDefault();
        startEdit();
        return;
      }
      lastNameTapRef.current = { key, at: now };
    };

  const cancelEdit = () => {
    setEditing(null);
    setEditingValue("");
    setEditingError(null);
  };
  const commitFolderEdit = async (folder: FolderDto) => {
    const next = editingValue.trim();
    if (!next) {
      setEditingError("Folder name is required");
      return;
    }
    if (next.length > 80) {
      setEditingError("Folder name must be 80 characters or less");
      return;
    }
    if (next === folder.name) {
      cancelEdit();
      return;
    }
    await onFolderRename(folder.folder_id, next);
    cancelEdit();
  };
  const commitGameEdit = async (game: LibraryGame) => {
    if (game.game_id == null) return;
    const next = editingValue.trim();
    if (!next || next === (game.game_description ?? "")) {
      cancelEdit();
      return;
    }
    if (isReservedGameName(next)) {
      setEditingError(
        `"${RESERVED_GAME_NAME}" is reserved for the in-progress game.`,
      );
      return;
    }
    await onGameRename(game.game_id, next, Boolean(game.complete));
    cancelEdit();
  };

  const renderFolderRow = (
    node: FolderTreeNode,
    depth: number,
  ): React.ReactNode => {
    if (q && !shownFolderIds?.has(node.folder.folder_id)) return null;
    const isOpen = q ? true : expanded.has(node.folder.folder_id);
    const folderGames = gamesToShow(node.folder.folder_id, node.folder);
    const hasGames = folderGames.length > 0;
    const hasChildFolders = node.children.length > 0;
    const hasChildren = hasChildFolders || hasGames;
    const canHaveChildren = node.depth < MAX_FOLDER_DEPTH;
    const childCount = childCountByFolderId.get(node.folder.folder_id) ?? 0;
    const gameCount = gameCountByFolderId.get(node.folder.folder_id) ?? 0;
    const isEmpty = childCount === 0 && gameCount === 0;
    const deleteTooltip = isEmpty
      ? undefined
      : `Cannot delete: contains ${[
          childCount > 0 ? `${childCount} subfolder(s)` : null,
          gameCount > 0 ? `${gameCount} game(s)` : null,
        ]
          .filter(Boolean)
          .join(" and ")}`;
    const isEditingThis =
      editing?.type === "folder" && editing.folderId === node.folder.folder_id;

    return (
      <React.Fragment key={`folder-${node.folder.folder_id}`}>
        <SortableTr
          id={`folder-${node.folder.folder_id}`}
          disabled={dndDisabled || !onReorderItems}
          className="border-b border-slate-100 bg-white hover:bg-slate-50"
        >
          <td
            className="w-1/3 whitespace-nowrap py-2 text-sm text-gray-800"
            style={{
              paddingLeft: NAME_BASE_PADDING_PX + depth * NAME_INDENT_PX,
            }}
          >
            <div className="flex items-center gap-2">
              <RowDragHandle />
              <button
                type="button"
                className="flex h-4 w-4 items-center justify-center text-gray-500"
                onClick={() => hasChildren && toggle(node.folder.folder_id)}
                aria-label={isOpen ? "Collapse" : "Expand"}
              >
                {hasChildren ? (
                  <ChevronRight
                    size={14}
                    color="#1a59eb"
                    className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                ) : null}
              </button>
              {/* Open only with something to show: a folder whose last game
                  was moved or deleted stays in `expanded`, and its chevron
                  (gone with its children) can't close it. */}
              <MacFolderIcon size={16} open={isOpen && hasChildren} />
              {isEditingThis ? (
                <div className="flex flex-1 flex-col gap-1">
                  <input
                    ref={inputRef}
                    value={editingValue}
                    onChange={(e) => {
                      setEditingValue(e.target.value);
                      if (editingError) setEditingError(null);
                    }}
                    onBlur={() => {
                      if (skipBlurCommitRef.current) {
                        skipBlurCommitRef.current = false;
                        return;
                      }
                      void commitFolderEdit(node.folder);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") {
                        skipBlurCommitRef.current = true;
                        cancelEdit();
                      }
                    }}
                    className="h-7 w-full max-w-[400px] rounded-md border border-blue-400 px-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                  {editingError ? (
                    <p className="text-xs text-red-600">{editingError}</p>
                  ) : null}
                </div>
              ) : (
                <span
                  className="flex-1 truncate"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    startEditFolder(node.folder);
                  }}
                  onTouchEnd={onNameTouchEnd(`folder-${node.folder.folder_id}`, () =>
                    startEditFolder(node.folder),
                  )}
                >
                  {node.folder.name}
                </span>
              )}
            </div>
          </td>
          <td className="w-px whitespace-nowrap px-6 py-2 text-sm text-gray-400" />
          <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-400" />
          <td className="w-10 py-2 pr-2" />
          <td className="w-px whitespace-nowrap py-2 pr-4 text-right">
            <div className="flex justify-end">
              <RowKebab
                title="Folder actions"
                items={[
                  canHaveChildren
                    ? {
                        key: "new",
                        label: "New",
                        icon: <FolderPlus size={14} />,
                        onClick: () => onFolderCreate(node.folder.folder_id),
                      }
                    : null,
                  onFolderMove
                    ? {
                        key: "move",
                        label: "Move",
                        icon: <FolderInput size={14} />,
                        onClick: () => onFolderMove?.(node.folder.folder_id),
                      }
                    : null,
                  {
                    key: "delete",
                    label: "Delete",
                    icon: <Trash2 size={14} />,
                    danger: true,
                    disabled: !isEmpty,
                    disabledTooltip: deleteTooltip,
                    onClick: () => onFolderDelete(node.folder.folder_id),
                  },
                ]}
              />
            </div>
          </td>
        </SortableTr>
        {creatingUnder === node.folder.folder_id ? (
          <NewFolderInlineRow
            depth={depth + 1}
            pendingName={pendingName ?? ""}
            onPendingNameChange={onPendingNameChange}
            onPendingNameSubmit={onPendingNameSubmit}
            onPendingNameCancel={onPendingNameCancel}
          />
        ) : null}
        {isOpen ? (
          <>
            {pinnedGamesFor(node.folder.folder_id, node.folder).map((game) =>
              renderGameRow(game, depth + 1),
            )}
            {itemsToRender(node.folder.folder_id, node.folder).map((item) => {
              if (item.kind === "game") {
                return renderGameRow(item.game, depth + 1);
              }
              const childNode = nodeByFolderId.get(item.folder.folder_id);
              return childNode ? renderFolderRow(childNode, depth + 1) : null;
            })}
          </>
        ) : null}
      </React.Fragment>
    );
  };

  const renderGameRow = (game: LibraryGame, depth: number): React.ReactNode => {
    if (game.game_id == null) return null;
    const isEditingThis =
      editing?.type === "game" && editing.gameId === game.game_id;
    const isSelected = selectedGameIds.has(game.game_id);
    // Games are ticked AND this row is one of them → its kebab acts on the
    // whole selection (in-game parity).
    const actsOnSelection = bulkActive && isSelected;
    return (
      <SortableTr
        key={`game-${game.game_id}`}
        id={`game-${game.game_id}`}
        disabled={dndDisabled || !onReorderItems || isCurrentGame(game)}
        className={`border-b border-slate-100 ${
          isSelected ? "bg-blue-50/40" : "bg-white hover:bg-slate-50"
        }`}
      >
        <td
          className="w-1/3 whitespace-nowrap py-2 text-sm text-gray-700"
          style={{
            paddingLeft: NAME_BASE_PADDING_PX + depth * NAME_INDENT_PX,
          }}
        >
          <div className="flex items-center gap-2">
            <RowDragHandle />
            {/* Chevron-width spacer: keeps handles in one column per level. */}
            <span className="h-4 w-4 shrink-0" aria-hidden />
            <File size={14} className="shrink-0 text-blue-600" />
            {isEditingThis ? (
              <div className="flex flex-col gap-1">
                <input
                  ref={inputRef}
                  value={editingValue}
                  onChange={(e) => {
                    setEditingValue(e.target.value);
                    if (editingError) setEditingError(null);
                  }}
                  onBlur={() => {
                    if (skipBlurCommitRef.current) {
                      skipBlurCommitRef.current = false;
                      return;
                    }
                    void commitGameEdit(game);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                    if (e.key === "Escape") {
                      skipBlurCommitRef.current = true;
                      cancelEdit();
                    }
                  }}
                  className="h-7 w-full max-w-[400px] rounded-md border border-blue-400 px-2 text-sm focus:border-blue-500 focus:outline-none"
                />
                {editingError ? (
                  <p className="text-xs text-red-600">{editingError}</p>
                ) : null}
              </div>
            ) : (
              <span
                className="block truncate"
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  startEditGame(game);
                }}
                onTouchEnd={onNameTouchEnd(`game-${game.game_id}`, () =>
                  startEditGame(game),
                )}
              >
                {game.game_description ?? ""}
              </span>
            )}
          </div>
        </td>
        <GameDateAndWeekCells game={game} onSave={onGameDateChange} today={today} />
        <td className="w-10 py-2 pr-2 text-right">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 accent-[#0275ff]"
            checked={isSelected}
            onChange={() => onToggleGameSelected(game.game_id!)}
            aria-label={`Select ${game.game_description ?? "game"}`}
          />
        </td>
        <td className="w-px whitespace-nowrap py-2 pr-4 text-right">
          <div className="flex justify-end">
            {/* Once games are ticked, EVERY ticked row's kebab acts on the
                whole selection — no trip to the header kebab (in-game
                parity). A row the coach did NOT tick keeps its own
                single-game actions, so Delete can never silently hit a set
                that excludes the row they clicked.

                No Rename: double-click the name to rename, as in-game. */}
            <RowKebab
              title={
                actsOnSelection
                  ? `Actions for ${selectedIdsArray.length} selected games`
                  : "Game actions"
              }
              items={
                actsOnSelection
                  ? [
                      ...exportKebabItems(selectedIdsArray),
                      {
                        key: "move",
                        label: "Move",
                        icon: <FolderInput size={14} />,
                        onClick: () => onBulkMove?.(selectedIdsArray),
                      },
                      {
                        key: "delete",
                        label: "Delete",
                        icon: <Trash2 size={14} />,
                        danger: true,
                        // The bulk handler keeps only completed games and says
                        // so when none qualify — same as the header kebab.
                        onClick: () => onBulkDelete?.(selectedIdsArray),
                      },
                    ]
                  : [
                      ...exportKebabItems([game.game_id!]),
                      {
                        key: "move",
                        label: "Move",
                        icon: <FolderInput size={14} />,
                        onClick: () => onGameMove(game.game_id!),
                      },
                      {
                        key: "delete",
                        label: "Delete",
                        icon: <Trash2 size={14} />,
                        danger: true,
                        disabled: !game.complete,
                        disabledTooltip: "Only completed games can be deleted.",
                        onClick: () =>
                          onGameDelete(game.game_id!, Boolean(game.complete)),
                      },
                    ]
              }
            />
          </div>
        </td>
      </SortableTr>
    );
  };

  const rootPinnedGames = pinnedGamesFor(null);
  const rootItems = itemsToRender(null);
  const isEmpty = rootPinnedGames.length === 0 && rootItems.length === 0;
  // Set iteration preserves insertion order, so this IS the order the user
  // selected the games in — merged exports honor it (first-selected game
  // names the file and leads the merged play list).
  const selectedIdsArray = useMemo(
    () => Array.from(selectedGameIds),
    [selectedGameIds],
  );
  const bulkActive = selectedIdsArray.length > 0;

  // Export actions shared between the per-row kebab (single game) and the
  // bulk kebab (merged multi-game). Mirrors platform-2.0's exportKebabItems.
  const exportKebabItems = (
    gameIds: number[],
    disabled = false,
  ): KebabItem[] => {
    const reportReady = Boolean(onGameReport && isReportReady?.(gameIds));
    return [
    onGameReport
      ? {
          key: "report",
          // Once a report is cached, the main action opens the preview and
          // the chevron reveals Download / Regenerate (mirrors in-game);
          // before that it's a single "Report" action that generates.
          label: reportReady ? "Preview" : (reportLabel ?? "Report"),
          icon: <SigmaIcon size={14} />,
          disabled,
          onClick: () => onGameReport(gameIds),
          submenu:
            !disabled && reportReady
              ? ([
                  onReportDownload && {
                    key: "report-download",
                    label: "Download",
                    icon: <FileDown size={14} />,
                    onClick: () => onReportDownload(gameIds),
                  },
                  onReportRegenerate && {
                    key: "report-regenerate",
                    label: "Regenerate",
                    icon: <RefreshCw size={14} />,
                    onClick: () => onReportRegenerate(gameIds),
                  },
                ].filter(Boolean) as KebabSubItem[])
              : undefined,
        }
      : null,
    onGameDownload
      ? {
          key: "download",
          label: "Download",
          icon: <Download size={14} />,
          disabled,
          onClick: () => onGameDownload(gameIds),
        }
      : null,
    onGameExportToDrive
      ? {
          key: "gdrive",
          label: "Export to G Drive",
          icon: <UploadCloud size={14} />,
          disabled,
          onClick: () => onGameExportToDrive(gameIds),
        }
      : null,
    ];
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="min-h-0 flex-1 overflow-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableRowIds}
            strategy={verticalListSortingStrategy}
          >
            <table className="min-w-full divide-y divide-slate-200">
          <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
            <tr>
              <th colSpan={3} className="px-4 py-2 text-left">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search
                      size={14}
                      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search"
                      aria-label="Search"
                      className="h-8 w-56 rounded-md border border-slate-200 bg-white pl-8 pr-7 text-xs font-normal text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none"
                    />
                    {query ? (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        aria-label="Clear search"
                        className="absolute right-1.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onFolderCreate(null)}
                    className="inline-flex h-8 shrink-0 items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-100"
                  >
                    New folder
                  </button>
                </div>
              </th>
              <th className="w-10 py-2 pr-2 text-right">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 accent-[#0275ff]"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={(e) =>
                    onToggleSelectAll(e.target.checked, visibleGameIds)
                  }
                  aria-label="Select all games"
                />
              </th>
              <th className="w-px whitespace-nowrap py-2 pr-4 text-right">
                <div className="flex justify-end">
                  <RowKebab
                    title="Bulk actions"
                    disabled={!bulkActive}
                    items={[
                      ...exportKebabItems(selectedIdsArray, !bulkActive),
                      {
                        key: "move",
                        label: "Move",
                        icon: <FolderInput size={14} />,
                        disabled: !bulkActive,
                        onClick: () => onBulkMove?.(selectedIdsArray),
                      },
                      {
                        key: "delete",
                        label: "Delete",
                        icon: <Trash2 size={14} />,
                        danger: true,
                        disabled: !bulkActive,
                        onClick: () => onBulkDelete?.(selectedIdsArray),
                      },
                    ]}
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {creatingUnder === null ? (
              <NewFolderInlineRow
                depth={0}
                pendingName={pendingName ?? ""}
                onPendingNameChange={onPendingNameChange}
                onPendingNameSubmit={onPendingNameSubmit}
                onPendingNameCancel={onPendingNameCancel}
              />
            ) : null}
            {rootPinnedGames.map((game) => renderGameRow(game, 0))}
            {rootItems.map((item) => {
              if (item.kind === "game") {
                return renderGameRow(item.game, 0);
              }
              const node = nodeByFolderId.get(item.folder.folder_id);
              return node ? renderFolderRow(node, 0) : null;
            })}
            {isEmpty ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-6 text-center text-sm text-gray-400"
                >
                  {q
                    ? `No folders or games match "${query.trim()}".`
                    : "No folders or games yet."}
                </td>
              </tr>
            ) : null}
          </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

// ── Sortable row + drag handle (ported from the in-game library) ───────

/**
 * Sortable table row: provides its drag listeners over context so the
 * RowDragHandle can live anywhere inside the row's cells (left edge).
 */
const RowDragContext = React.createContext<{
  listeners: ReturnType<typeof useSortable>["listeners"];
  attributes: ReturnType<typeof useSortable>["attributes"] | undefined;
  enabled: boolean;
} | null>(null);

const SortableTr: React.FC<{
  id: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ id, disabled = false, className, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  return (
    <RowDragContext.Provider
      value={{ listeners, attributes, enabled: !disabled }}
    >
      <tr
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.6 : 1,
        }}
        className={className}
      >
        {children}
      </tr>
    </RowDragContext.Provider>
  );
};

const RowDragHandle: React.FC = () => {
  const ctx = React.useContext(RowDragContext);
  // Spacer keeps pinned / non-draggable rows aligned with draggable ones.
  if (!ctx?.enabled) return <span className="h-4 w-4 shrink-0" aria-hidden />;
  return (
    <button
      type="button"
      aria-label="Drag to reorder"
      className="inline-flex h-4 w-4 shrink-0 cursor-grab items-center justify-center text-gray-300 hover:text-gray-500 active:cursor-grabbing"
      style={{ touchAction: "none" }}
      {...ctx.attributes}
      {...ctx.listeners}
    >
      <GripVertical size={12} />
    </button>
  );
};

// ── Row kebab menu ─────────────────────────────────────────────────────

type KebabSubItem = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
};

type KebabItem = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  onClick: () => void;
  /** Nested actions revealed by a chevron on the item's right edge. */
  submenu?: KebabSubItem[];
} | null;

const RowKebab: React.FC<{
  title: string;
  items: Array<KebabItem>;
  disabled?: boolean;
}> = ({ title, items, disabled = false }) => {
  const visible = items.filter((i): i is Exclude<KebabItem, null> => i !== null);
  // Which item's chevron submenu is currently expanded (only one at a time).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  return (
    <Menu as="div" className="relative">
      <Menu.Button
        as="button"
        disabled={disabled}
        className={`rounded p-1 ${
          disabled
            ? "cursor-not-allowed text-gray-300"
            : "text-gray-500 hover:bg-gray-200 hover:text-gray-700"
        }`}
        onClick={(e) => e.stopPropagation()}
        title={title}
      >
        <MoreVertical size={14} />
      </Menu.Button>
      <Menu.Items
        anchor="bottom end"
        className="absolute right-0 z-50 mt-1 w-48 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg focus:outline-none"
      >
        {visible.map((item) => {
          const hasSubmenu = !!item.submenu && item.submenu.length > 0;
          const isExpanded = expandedKey === item.key;
          return (
            <div key={item.key}>
              <div className="flex items-stretch">
                <Menu.Item disabled={item.disabled}>
                  {({ active, disabled: itemDisabled }) => (
                    <button
                      type="button"
                      className={`flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-sm ${
                        item.danger
                          ? itemDisabled
                            ? "cursor-not-allowed text-red-300"
                            : active
                              ? "bg-red-50 text-red-600"
                              : "text-red-600"
                          : itemDisabled
                            ? "cursor-not-allowed text-gray-400"
                            : active
                              ? "bg-gray-100"
                              : ""
                      }`}
                      title={itemDisabled ? item.disabledTooltip : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!item.disabled) item.onClick();
                      }}
                    >
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                        {item.icon}
                      </span>
                      <span className="truncate">{item.label}</span>
                    </button>
                  )}
                </Menu.Item>
                {hasSubmenu ? (
                  <button
                    type="button"
                    aria-label="More report options"
                    className="flex items-center border-l border-gray-100 px-2 text-gray-500 hover:bg-gray-100 focus:outline-none"
                    // Plain button (not a Menu.Item) so toggling the submenu
                    // doesn't close the kebab.
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setExpandedKey((prev) =>
                        prev === item.key ? null : item.key,
                      );
                    }}
                  >
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                ) : null}
              </div>
              {hasSubmenu && isExpanded
                ? item.submenu!.map((sub) => (
                    <Menu.Item key={sub.key}>
                      {({ active }) => (
                        <button
                          type="button"
                          className={`flex w-full min-w-0 items-center gap-2 py-2 pl-9 pr-3 text-left text-sm ${
                            active ? "bg-gray-100" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            sub.onClick();
                          }}
                        >
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                            {sub.icon}
                          </span>
                          <span className="truncate">{sub.label}</span>
                        </button>
                      )}
                    </Menu.Item>
                  ))
                : null}
            </div>
          );
        })}
      </Menu.Items>
    </Menu>
  );
};

// ── Inline "new folder" row ────────────────────────────────────────────

const NewFolderInlineRow: React.FC<{
  depth: number;
  pendingName: string;
  onPendingNameChange?: (next: string) => void;
  onPendingNameSubmit?: () => void;
  onPendingNameCancel?: () => void;
}> = ({
  depth,
  pendingName,
  onPendingNameChange,
  onPendingNameSubmit,
  onPendingNameCancel,
}) => {
  const skipBlurSubmitRef = useRef(false);
  return (
    <tr className="border-b border-slate-100 bg-blue-50/30">
      <td
        className="w-1/3 whitespace-nowrap py-2 text-sm text-gray-800"
        style={{
          paddingLeft: NAME_BASE_PADDING_PX + depth * NAME_INDENT_PX,
        }}
      >
        <div className="flex items-center gap-2">
          <div className="h-4 w-4" aria-hidden="true" />
          <MacFolderIcon size={16} />
          <input
            autoFocus
            value={pendingName}
            onChange={(e) => onPendingNameChange?.(e.target.value)}
            onBlur={() => {
              if (skipBlurSubmitRef.current) {
                skipBlurSubmitRef.current = false;
                return;
              }
              onPendingNameSubmit?.();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                skipBlurSubmitRef.current = true;
                onPendingNameCancel?.();
              }
            }}
            placeholder="New folder"
            className="h-7 w-full max-w-[300px] rounded-md border border-blue-400 px-2 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      </td>
      <td className="w-px whitespace-nowrap px-6 py-2 text-sm text-gray-400">—</td>
      <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-400" />
      <td className="w-10 py-2 pr-2" />
      <td className="w-px whitespace-nowrap py-2 pr-4 text-right" />
    </tr>
  );
};
